import type { MouseEvent } from 'react'
import type { Company } from '@/constants/companies'
import type { TableColumnKey } from '@/components/companies/table/table-columns'

import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import Tag from '@/components/ui/tag'
import SegmentBar from '@/components/generals/segment-bar'
import Sparkline from '@/components/generals/sparkline'
import { Checkbox } from '@/components/ui/checkbox'
import { TableCell, TableRow } from '@/components/ui/table'
import { ownerByName, tagTone } from '@/constants/companies'
import { formatDate, formatMoney, splitTags } from '@/lib/companies'
import { cn } from '@/lib/utils'
import { TABLE_CELL_CLASS, TABLE_ROW_CLASS, columnClass } from '@/components/companies/table/table-columns'
import CalendarIcon from '@/assets/images/_common/calendar.svg'
import DotsIcon from '@/assets/images/companies/table/dots-horizontal.svg'

interface CompanyRowProps {
    company: Company
    selected: boolean
    active: boolean
    onToggle: () => void
    onOpen: () => void
    onOpenOwner: () => void
}

function cellClass(key: TableColumnKey): string {
    return cn(TABLE_CELL_CLASS, columnClass(key))
}

function stop(event: MouseEvent) {
    event.stopPropagation()
}

export default function CompanyRow({
    company,
    selected,
    active,
    onToggle,
    onOpen,
    onOpenOwner
}: CompanyRowProps) {
    const owner = ownerByName(company.owner)
    const { visible, hidden } = splitTags(company.tags)

    return (
        <TableRow
            role='row'
            onClick={onOpen}
            data-active={active || selected}
            className={cn(
                TABLE_ROW_CLASS,
                'hover:bg-card/60 data-[active=true]:border-card data-[active=true]:bg-card cursor-pointer'
            )}
        >
            <TableCell role='cell' className={cellClass('name')}>
                <span className='flex items-center gap-5'>
                    <Checkbox
                        checked={selected}
                        onCheckedChange={onToggle}
                        onClick={stop}
                        aria-label={`Select ${company.name}`}
                    />
                    <span data-mdlfy-cms={`companies:${company.id}:name`}>
                        {company.name}
                    </span>
                </span>
            </TableCell>
            <TableCell role='cell' className={cellClass('segment')}>
                <span className='flex items-center gap-[3px]'>
                    {visible.map(tag => (
                        <Tag key={tag} tone={tagTone(tag)}>
                            {tag}
                        </Tag>
                    ))}
                    {hidden > 0 && (
                        <Tag tone='neutral' size='sm'>
                            +{hidden}
                        </Tag>
                    )}
                </span>
            </TableCell>
            <TableCell role='cell' className={cellClass('owner')} onClick={stop}>
                <Button
                    variant='ghost'
                    size='none'
                    onClick={onOpenOwner}
                    aria-label={`Open ${owner.name} profile`}
                    className='text-foreground -mx-1.5 gap-1.5 px-1.5 py-1 font-normal'
                >
                    <Avatar src={owner.avatar} alt='' />
                    <span data-mdlfy-cms={`companies:${company.id}:owner`}>
                        {company.owner}
                    </span>
                </Button>
            </TableCell>
            <TableCell role='cell' className={cellClass('openDeals')}>
                {company.openDeals}
            </TableCell>
            <TableCell role='cell' className={cellClass('pipelineValue')}>
                <span className='flex items-center gap-1'>
                    <span className='text-muted-foreground'>$</span>
                    <span>{formatMoney(company.pipelineValue)}</span>
                </span>
            </TableCell>
            <TableCell role='cell' className={cellClass('winProbability')}>
                <span className='flex items-center gap-2'>
                    <SegmentBar percent={company.winProbability} className='w-[74px]' />
                    <span className='w-[4ch] text-right'>
                        <span>{company.winProbability}</span>%
                    </span>
                </span>
            </TableCell>
            <TableCell role='cell' className={cellClass('trend')}>
                <Sparkline values={company.trend} />
            </TableCell>
            <TableCell role='cell' className={cellClass('lastInteraction')}>
                <span className='flex items-center gap-1'>
                    <CalendarIcon aria-hidden className='text-foreground size-3.5 shrink-0' />
                    <span className='tabular-nums'>
                        {formatDate(company.lastInteraction.date)}
                    </span>
                    <span aria-hidden className='mx-[3px] h-2 w-px bg-white/15' />
                    <span data-mdlfy-cms={`companies:${company.id}:last_interaction_label`}>
                        {company.lastInteraction.label}
                    </span>
                </span>
            </TableCell>
            <TableCell role='cell' className={cellClass('action')} onClick={stop}>
                <Button
                    variant='ghost'
                    size='icon-sm'
                    className={cn('text-foreground', active && 'bg-white/6')}
                    aria-label={`Open ${company.name} details`}
                    onClick={onOpen}
                >
                    <DotsIcon aria-hidden className='size-3' />
                </Button>
            </TableCell>
        </TableRow>
    )
}
